import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MagnifyingGlassIcon,
    PlusIcon,
    AcademicCapIcon,
    BookOpenIcon,
    PencilSquareIcon,
    TrashIcon,
    ChevronRightIcon,
    DocumentDuplicateIcon,
    SparklesIcon,
    XMarkIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import FilterDropdown from '../../components/common/FilterDropdown';
import BlueprintCreator from '../../components/teacher/BlueprintCreator';
import LessonPlanModal from '../../components/teacher/LessonPlanModal';
import { generateLessonPlanPDF, generateSOWPDF } from '../../utils/pdfGenerators';
import { CBC_LEVELS, getLevelData } from '../../utils/cbcUtils';
import FloatingManualButton from '../../components/common/FloatingManualButton';

const LessonPlanTemplateManager = () => {
    const navigate = useNavigate();
    const { get, del } = useApi();
    const { showToast } = useAppState();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [assessmentTemplates, setAssessmentTemplates] = useState([]);
    const [schemesOfWork, setSchemesOfWork] = useState([]);
    const [terms, setTerms] = useState([]);
    const [learningAreas, setLearningAreas] = useState([]);
    const [strands, setStrands] = useState([]);
    const [subStrands, setSubStrands] = useState([]);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, templateId: null, type: 'lesson_plan' });
    const [editTemplateData, setEditTemplateData] = useState(null);
    const [editLessonPlan, setEditLessonPlan] = useState(null);
    const [exportingSowId, setExportingSowId] = useState(null);

    // Track active performance band view per assessment rubric card
    const [activeBands, setActiveBands] = useState({});

    const handleBandClick = (rubricId, bandCode) => {
        setActiveBands(prev => {
            const current = prev[rubricId] || 'INTENT';
            return {
                ...prev,
                [rubricId]: current === bandCode ? 'INTENT' : bandCode
            };
        });
    };

    const getDisplayText = (rubric, currentBand) => {
        switch (currentBand) {
            case 'EE':
                return { label: 'Exceeding Expectations (EE)', text: rubric.criteria_ee || 'No specific criteria defined for Exceeding Expectations.' };
            case 'ME':
                return { label: 'Meeting Expectations (ME)', text: rubric.criteria_me || 'No specific criteria defined for Meeting Expectations.' };
            case 'AE':
                return { label: 'Approaching Expectations (AE)', text: rubric.criteria_ae || 'No specific criteria defined for Approaching Expectations.' };
            case 'BE':
                return { label: 'Below Expectations (BE)', text: rubric.criteria_be || 'No specific criteria defined for Below Expectations.' };
            default:
                return { label: 'Strategic Intent', text: rubric.description || 'No strategic intent specified.' };
        }
    };

    // Consolidated Blueprint Modal
    const [blueprintModal, setBlueprintModal] = useState({ isOpen: false, tab: 'assessment' });
    const [creationSelectionOpen, setCreationSelectionOpen] = useState(false);
    const [isCreatingLessonPlan, setIsCreatingLessonPlan] = useState(false);

    const [filters, setFilters] = useState({
        type: 'all', // all | lesson_plan | assessment | sow
        learningArea: '',
        strand: '',
        subStrand: '',
        term: '',
        search: ''
    });

    const [strsCache, setStrsCache] = useState({});
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const fetchInitialData = useCallback(async () => {
        try {
            setLoading(true);
            const [areas, termsData] = await Promise.all([
                get('/teachers/api/courses/'),
                get('/api/academic-terms/')
            ]);
            setLearningAreas(areas.courses || []);
            setTerms(Array.isArray(termsData) ? termsData : (termsData?.results || []));
        } catch (error) {
            console.error('Error fetching initial data:', error);
        } finally {
            setLoading(false);
        }
    }, [get]);

    const handleExportPDF = async (templateId) => {
        try {
            setLoading(true);
            const data = await get(`/api/cbc/lesson-plans/${templateId}/`);
            await generateLessonPlanPDF(data);
            showToast('PDF downloaded successfully');
        } catch (error) {
            showToast('Failed to export PDF', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleExportSOWPDF = async (sowId) => {
        try {
            setExportingSowId(sowId);
            showToast('Preparing Scheme of Work PDF...', 'info');
            const data = await get(`/api/cbc/schemes-of-work/${sowId}/`);
            await generateSOWPDF(data, data.weekly_breakdown || []);
            showToast('Scheme of Work PDF downloaded successfully!', 'success');
        } catch (error) {
            console.error('Failed to export SOW PDF:', error);
            showToast('Failed to export Scheme of Work PDF', 'error');
        } finally {
            setExportingSowId(null);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(filters.search);
        }, 600);
        return () => clearTimeout(timer);
    }, [filters.search]);

    const fetchTemplates = useCallback(async () => {
        try {
            setLoading(true);
            const promises = [];

            // Fetch Lesson Plans if applicable
            if (filters.type === 'all' || filters.type === 'lesson_plan') {
                let lpUrl = `/api/cbc/lesson-plans/?is_template=true`;
                if (filters.learningArea) lpUrl += `&course=${filters.learningArea}`;
                if (filters.subStrand) lpUrl += `&sub_strand=${filters.subStrand}`;
                if (debouncedSearch) lpUrl += `&search=${encodeURIComponent(debouncedSearch)}`;
                promises.push(get(lpUrl).then(data => ({ type: 'lesson_plan', data: Array.isArray(data) ? data : [] })));
            } else {
                promises.push(Promise.resolve({ type: 'lesson_plan', data: [] }));
            }

            // Fetch Assessment Templates if applicable
            if (filters.type === 'all' || filters.type === 'assessment') {
                let atUrl = `/api/cbc/templates/`;
                const params = [];
                if (filters.learningArea) params.push(`learning_area=${filters.learningArea}`);
                if (filters.strand) params.push(`strand=${filters.strand}`); // ADDED STRAND FILTER
                if (debouncedSearch) params.push(`search=${encodeURIComponent(debouncedSearch)}`);
                if (params.length > 0) atUrl += `?${params.join('&')}`;
                promises.push(get(atUrl).then(data => ({ type: 'assessment', data: Array.isArray(data) ? data : [] })));
            } else {
                promises.push(Promise.resolve({ type: 'assessment', data: [] }));
            }

            // Fetch Schemes of Work if applicable (Strictly isolated to teacher's created SOWs)
            if (filters.type === 'all' || filters.type === 'sow') {
                let sowUrl = `/api/cbc/schemes-of-work/?only_mine=true`;
                if (filters.learningArea) sowUrl += `&learning_area=${filters.learningArea}`;
                if (filters.term) sowUrl += `&term=${filters.term}`;
                if (debouncedSearch) sowUrl += `&search=${encodeURIComponent(debouncedSearch)}`;
                promises.push(get(sowUrl).then(data => ({
                    type: 'sow',
                    data: Array.isArray(data) ? data : (data?.results || [])
                })));
            } else {
                promises.push(Promise.resolve({ type: 'sow', data: [] }));
            }

            const results = await Promise.all(promises);
            results.forEach(res => {
                if (res.type === 'lesson_plan') setTemplates(res.data);
                if (res.type === 'assessment') {
                    let items = res.data;
                    if (debouncedSearch) {
                        const s = debouncedSearch.toLowerCase();
                        items = items.filter(t => 
                            (t.name && t.name.toLowerCase().includes(s)) ||
                            (t.description && t.description.toLowerCase().includes(s)) ||
                            (t.criteria_ee && t.criteria_ee.toLowerCase().includes(s)) ||
                            (t.criteria_me && t.criteria_me.toLowerCase().includes(s))
                        );
                    }
                    setAssessmentTemplates(items);
                }
                if (res.type === 'sow') {
                    let items = res.data;
                    // Strict teacher guard: only display SOW created by this teacher
                    const teacherId = user?.teacher_id || user?.teacher?.id;
                    if (teacherId) {
                        items = items.filter(s => {
                            const creatorId = s.created_by?.id || s.created_by;
                            return String(creatorId) === String(teacherId);
                        });
                    }
                    if (debouncedSearch) {
                        const s = debouncedSearch.toLowerCase();
                        items = items.filter(item =>
                            (item.title && item.title.toLowerCase().includes(s)) ||
                            (item.learning_area_name && item.learning_area_name.toLowerCase().includes(s)) ||
                            (item.term_name && item.term_name.toLowerCase().includes(s))
                        );
                    }
                    setSchemesOfWork(items);
                }
            });
        } catch (error) {
            console.error('Error fetching blueprints:', error);
            showToast('Failed to load blueprints', 'error');
        } finally {
            setLoading(false);
        }
    }, [get, filters.type, filters.learningArea, filters.strand, filters.subStrand, filters.term, debouncedSearch, showToast, user]);

    // Fetch templates when filters change
    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const handleAreaChange = useCallback(async (areaId) => {
        setFilters(prevFilters => ({ ...prevFilters, learningArea: areaId, strand: '', subStrand: '' }));
        setStrands([]);
        setSubStrands([]);

        if (!areaId) return;

        // Smart Caching: Check if we already have strands for this area
        if (strsCache[areaId]) {
            setStrands(strsCache[areaId]);
            return;
        }

        try {
            const areaData = await get(`/teachers/api/courses/${areaId}/`);
            // API returns 'modules' which maps to strands
            const availableStrands = areaData.modules || areaData.strands || [];

            setStrands(availableStrands);

            // Update Cache
            setStrsCache(prev => ({
                ...prev,
                [areaId]: availableStrands
            }));
        } catch (error) {
            console.error('Error fetching strands:', error);
            showToast('Failed to load curriculum strands', 'error');
        }
    }, [get, showToast, strsCache]);

    const handleStrandChange = (strandId) => {
        // Find strand safely handling string/number types
        const selectedStrand = strands.find(s => String(s.id) === String(strandId));
        setFilters(prev => ({ ...prev, strand: strandId, subStrand: '' }));
        setSubStrands(selectedStrand?.sub_strands || []);
    };

    const confirmDelete = (id, type = 'lesson_plan') => {
        setDeleteModal({ isOpen: true, templateId: id, type });
    };

    const handleDeleteTemplate = async () => {
        const { templateId, type } = deleteModal;
        if (!templateId) return;

        try {
            if (type === 'lesson_plan') {
                await del(`/api/cbc/lesson-plans/${templateId}/`);
                setTemplates(templates.filter(t => t.id !== templateId));
            } else {
                await del(`/api/cbc/templates/${templateId}/`);
                setAssessmentTemplates(assessmentTemplates.filter(t => t.id !== templateId));
            }
            showToast('Blueprint deleted successfully');
            setDeleteModal({ isOpen: false, templateId: null, type: 'lesson_plan' });
        } catch (error) {
            console.error('Error deleting blueprint:', error);
            showToast(error.message || 'Failed to delete blueprint', 'error');
        }
    };

    // Filter Options for custom FilterDropdown components
    const typeOptions = [
        { name: 'All Templates', value: 'all' },
        { name: 'Lesson Plans', value: 'lesson_plan' },
        { name: 'Assessment Rubrics', value: 'assessment' },
        { name: 'Schemes of Work', value: 'sow' }
    ];

    const termOptions = useMemo(() => [
        { name: 'All Academic Terms', value: '' },
        ...terms.map(t => ({
            name: `${t.name || `Term ${t.term_number}`} ${t.year?.name || t.academic_year_name || ''} ${t.is_current ? '• (Current)' : ''}`.trim(),
            value: t.id.toString()
        }))
    ], [terms]);

    const learningAreaOptions = useMemo(() => 
        learningAreas.filter(a => !a.stream).map(area => ({
            name: `${area.name} - ${area.grade_level_name}`,
            value: area.id.toString()
        }))
    , [learningAreas]);

    const strandOptions = useMemo(() => 
        strands.map(strand => ({
            name: strand.name,
            value: strand.id.toString()
        }))
    , [strands]);

    const subStrandOptions = useMemo(() => 
        subStrands.map(sub => ({
            name: sub.name,
            value: sub.id.toString()
        }))
    , [subStrands]);

    return (
        <div className="min-h-screen bg-[#F8FAFC] p-4 lg:p-8">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Rich Header with Create Button & Search Input on the Right */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                    <div className="space-y-2 max-w-xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFC425]/10 text-[#18216D] text-[10px] font-black uppercase tracking-[0.2em] rounded-full border border-[#FFC425]/20">
                            <SparklesIcon className="w-3 h-3" />
                            Curriculum Repository
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-black text-[#18216D] uppercase tracking-tighter">
                            Lesson <span className="text-[#3B82F6]">Templates</span>
                        </h1>
                        <p className="text-slate-500 font-medium italic text-sm sm:text-base">Create, refine and reuse your lesson plans and assessment rubrics.</p>
                    </div>

                    <div className="flex flex-col items-start md:items-end gap-3 w-full md:w-auto shrink-0">
                        <div className="relative">
                            <button
                                onClick={() => setCreationSelectionOpen(true)}
                                className="flex items-center gap-3 px-7 py-3.5 bg-[#18216D] text-white font-black rounded-2xl hover:bg-[#0D164F] shadow-xl shadow-indigo-900/20 active:scale-95 transition-all uppercase tracking-widest text-[11px]"
                            >
                                <PlusIcon className="w-5 h-5 stroke-[3]" />
                                Create Template
                            </button>

                            {/* Creation Selection Dropdown Modal */}
                            {creationSelectionOpen && (
                                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
                                    <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="text-xl font-black text-[#18216D]">Create Template</h3>
                                            <button onClick={() => setCreationSelectionOpen(false)} className="p-2 text-slate-400 hover:text-red-500 rounded-full hover:bg-slate-50 transition-colors">
                                                <XMarkIcon className="w-6 h-6" />
                                            </button>
                                        </div>
                                        <p className="text-sm font-medium text-slate-500 mb-6">Choose the type of template you would like to create.</p>
                                        <div className="space-y-4">
                                            <button
                                                onClick={() => {
                                                    setCreationSelectionOpen(false);
                                                    setIsCreatingLessonPlan(true);
                                                }}
                                                className="w-full text-left px-6 py-5 bg-blue-50/50 hover:bg-blue-50 border border-blue-100 rounded-2xl transition-all flex items-center gap-4 group"
                                            >
                                                <div className="p-3 bg-blue-100 text-blue-600 rounded-xl group-hover:scale-110 transition-transform">
                                                    <BookOpenIcon className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <div className="text-base font-black text-[#18216D]">Lesson Plan Template</div>
                                                    <div className="text-xs font-medium text-slate-500 mt-1">Create a reusable lesson plan structure</div>
                                                </div>
                                            </button>

                                            <button
                                                onClick={() => {
                                                    setCreationSelectionOpen(false);
                                                    setBlueprintModal({ isOpen: true, tab: 'assessment' });
                                                }}
                                                className="w-full text-left px-6 py-5 bg-amber-50/50 hover:bg-amber-50 border border-amber-100 rounded-2xl transition-all flex items-center gap-4 group"
                                            >
                                                <div className="p-3 bg-amber-100 text-amber-600 rounded-xl group-hover:scale-110 transition-transform">
                                                    <AcademicCapIcon className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <div className="text-base font-black text-[#18216D]">Assessment Rubric</div>
                                                    <div className="text-xs font-medium text-slate-500 mt-1">Create an EE/ME/AE/BE assessment template</div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Search input placed below create template button */}
                        <div className="relative group w-full md:w-80">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                                <MagnifyingGlassIcon className="w-4 h-4" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search templates..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-xs font-bold text-[#18216D]"
                            />
                        </div>
                    </div>
                </div>

                {/* Filter Controls Section (Using Custom FilterDropdown Components) */}
                <div className="bg-white rounded-[1.5rem] p-5 shadow-sm border border-slate-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Template Type</label>
                            <FilterDropdown
                                value={filters.type}
                                onChange={(val) => setFilters({ ...filters, type: val })}
                                options={typeOptions}
                                placeholder="All Templates"
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Academic Term</label>
                            <FilterDropdown
                                value={filters.term}
                                onChange={(val) => setFilters({ ...filters, term: val })}
                                options={termOptions}
                                placeholder="All Terms"
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Learning Area</label>
                            <FilterDropdown
                                value={filters.learningArea ? filters.learningArea.toString() : ''}
                                onChange={(val) => handleAreaChange(val)}
                                options={learningAreaOptions}
                                placeholder="All Learning Areas"
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Strand</label>
                            <FilterDropdown
                                value={filters.strand ? filters.strand.toString() : ''}
                                onChange={(val) => handleStrandChange(val)}
                                options={strandOptions}
                                placeholder="All Strands"
                                disabled={!filters.learningArea || filters.type === 'sow'}
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Sub-strand</label>
                            <FilterDropdown
                                value={filters.subStrand ? filters.subStrand.toString() : ''}
                                onChange={(val) => setFilters({ ...filters, subStrand: val })}
                                options={subStrandOptions}
                                placeholder="All Sub-strands"
                                disabled={!filters.strand || filters.type === 'sow'}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>

                {/* Templates Grid */}
                {loading ? (
                    <div className="py-32 flex flex-col items-center">
                        <LoadingSpinner />
                        <p className="mt-4 text-xs font-black text-indigo-300 uppercase tracking-widest animate-pulse">Accessing Vault...</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {templates.length === 0 && assessmentTemplates.length === 0 && schemesOfWork.length === 0 ? (
                            <div className="col-span-full py-32 text-center bg-white rounded-[3rem] border-4 border-dashed border-slate-100 shadow-inner">
                                <div className="w-24 h-24 bg-slate-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8">
                                    <DocumentDuplicateIcon className="w-12 h-12 text-slate-200" />
                                </div>
                                <h3 className="text-2xl font-black text-slate-300 tracking-tight">Archives Empty</h3>
                                <p className="text-slate-300 mt-2 font-medium italic">Try adjusting your filters or create your first blueprint to get started.</p>
                            </div>
                        ) : (
                            <>
                                {/* Render Lesson Plans */}
                                {templates.map(template => (
                                    <div key={template.id} className="bg-white rounded-[2.5rem] border border-slate-100 p-8 hover:shadow-[0_25px_60px_-15px_rgba(24,33,109,0.12)] hover:border-indigo-100 transition-all group flex flex-col h-full relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/30 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-125"></div>
                                        <div className="absolute top-4 left-8 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[8px] font-black uppercase tracking-widest">Lesson Plan</div>

                                        <div className="flex items-start justify-between mb-8 mt-6 relative z-10">
                                            <div className="p-4 bg-blue-50/50 rounded-2xl text-[#18216D] group-hover:bg-[#18216D] group-hover:text-white transition-all shadow-inner">
                                                <BookOpenIcon className="w-7 h-7 stroke-[2]" />
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setEditLessonPlan(template)}
                                                    className="p-3 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-2xl transition-all border border-indigo-100 shadow-sm"
                                                    title="Edit Plan Details"
                                                >
                                                    <PencilSquareIcon className="w-6 h-6 stroke-[2]" />
                                                </button>
                                                <button
                                                    onClick={() => handleExportPDF(template.id)}
                                                    className="p-3 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-2xl transition-all border border-transparent hover:border-emerald-100 shadow-sm"
                                                    title="Download PDF"
                                                >
                                                    <ArrowDownTrayIcon className="w-6 h-6 stroke-[2]" />
                                                </button>
                                                <button
                                                    onClick={() => confirmDelete(template.id, 'lesson_plan')}
                                                    className="p-3 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-2xl transition-all border border-transparent hover:border-rose-100 shadow-sm"
                                                    title="Archive to Trash"
                                                >
                                                    <TrashIcon className="w-6 h-6" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex-1 relative z-10">
                                            <h3 className="text-2xl font-black text-[#18216D] line-clamp-2 mb-4 tracking-tighter leading-tight italic">
                                                {template.title}
                                            </h3>
                                            <div className="flex flex-col gap-2 mb-6">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-[#FFC425]"></span>
                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                        {template.learning_area_name}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                                    <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.15em] italic">
                                                        {template.grade_level_name || 'All Grades'} {template.stream_name ? `- ${template.stream_name}` : ''}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                                                    <span className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.15em] italic">
                                                        {new Date(template.lesson_date || template.created_at || new Date()).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-6 pt-4 border-t border-slate-50 relative z-10">
                                            <button
                                                onClick={() => navigate(`/teacher/lesson-plan/editor/${template.id}`)}
                                                className="w-full flex items-center justify-center gap-3 py-3 bg-indigo-50 hover:bg-indigo-100 text-[#18216D] rounded-xl text-xs font-black transition-colors uppercase tracking-[0.2em]"
                                            >
                                                View Design
                                                <ChevronRightIcon className="w-5 h-5 stroke-[3]" />
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {/* Render Assessment Templates */}
                                {assessmentTemplates.map(template => {
                                    const currentBand = activeBands[template.id] || 'INTENT';
                                    const displayContent = getDisplayText(template, currentBand);

                                    return (
                                        <div key={template.id} className="bg-white rounded-[2.5rem] border border-[#FFC425]/30 p-8 hover:shadow-[0_25px_60px_-15px_rgba(255,196,37,0.15)] hover:border-[#FFC425] transition-all group flex flex-col justify-between h-full relative overflow-hidden">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFC425]/10 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-125"></div>
                                            <div className="absolute top-4 left-8 px-3 py-1 bg-[#FFC425]/10 text-[#18216D] rounded-full text-[8px] font-black uppercase tracking-widest">Assessment Rubric</div>

                                            <div>
                                                <div className="flex items-start justify-between mb-8 mt-6 relative z-10">
                                                    <div className="p-4 bg-[#FFC425]/10 rounded-2xl text-[#18216D] group-hover:bg-[#FFC425] transition-all shadow-inner">
                                                        <AcademicCapIcon className="w-7 h-7 stroke-[2]" />
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setEditTemplateData(template);
                                                                setBlueprintModal({ isOpen: true, tab: 'assessment' });
                                                            }}
                                                            className="p-3 text-slate-400 hover:text-indigo-600 hover:bg-slate-50 rounded-2xl transition-all border border-transparent hover:border-slate-100 shadow-sm"
                                                            title="Refine Blueprint"
                                                        >
                                                            <PencilSquareIcon className="w-6 h-6" />
                                                        </button>
                                                        <button
                                                            onClick={() => confirmDelete(template.id, 'assessment')}
                                                            className="p-3 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-2xl transition-all border border-transparent hover:border-rose-100 shadow-sm"
                                                            title="Archive to Trash"
                                                        >
                                                            <TrashIcon className="w-6 h-6" />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="flex-1 relative z-10">
                                                    <h3 className="text-2xl font-black text-[#18216D] line-clamp-2 mb-4 tracking-tighter leading-tight">
                                                        {template.name}
                                                    </h3>
                                                    <div className="flex flex-col gap-2 mb-6">
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-[#18216D]"></span>
                                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                                                {template.learning_area_name}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                                            <span className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.15em] italic">
                                                                {template.grade_level_name}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="mb-6 space-y-1 min-h-[80px]">
                                                        {currentBand !== 'INTENT' && (
                                                            <p className={`text-[10px] font-black uppercase tracking-widest ${getLevelData(currentBand)?.color || 'text-[#18216D]'}`}>
                                                                {displayContent.label}
                                                            </p>
                                                        )}
                                                        <p className="text-slate-600 text-sm italic font-medium leading-relaxed">{displayContent.text}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Full-Width Grade Bands (EE, ME, AE, BE) */}
                                            <div className="grid grid-cols-4 gap-1.5 w-full mt-4 pt-4 border-t border-slate-100 relative z-10">
                                                {CBC_LEVELS.map((level) => {
                                                    const isActive = currentBand === level.code;
                                                    const levelInfo = getLevelData(level.code);

                                                    return (
                                                        <button
                                                            key={level.code}
                                                            type="button"
                                                            onClick={() => handleBandClick(template.id, level.code)}
                                                            className={`py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all text-center border ${
                                                                isActive
                                                                    ? `${levelInfo.bg || 'bg-[#18216D]'} ${levelInfo.color || 'text-[#18216D]'} ${levelInfo.border || 'border-[#18216D]'} shadow-sm font-bold scale-[1.02]`
                                                                    : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100 hover:text-[#18216D]'
                                                            }`}
                                                            title={level.label}
                                                        >
                                                            {level.code}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Render Schemes of Work */}
                                {schemesOfWork.map(sow => {
                                    const isExporting = exportingSowId === sow.id;
                                    const courseId = sow.learning_area?.id || sow.learning_area;
                                    const rawTitle = sow.title || sow.learning_area_name || 'Scheme of Work';
                                    const cleanTitle = rawTitle.replace(/\s*\([^)]*\)\s*$/, '').trim();
                                    const totalWeeks = sow.total_weeks != null 
                                        ? sow.total_weeks 
                                        : (Array.isArray(sow.weekly_breakdown) ? new Set(sow.weekly_breakdown.map(w => w.week_number || w.week)).size : 0);
                                    const totalLessons = sow.total_lessons != null 
                                        ? sow.total_lessons 
                                        : (Array.isArray(sow.weekly_breakdown) ? sow.weekly_breakdown.length : 0);

                                    return (
                                        <div key={`sow-${sow.id}`} className="bg-white rounded-[2.5rem] border border-slate-100 p-8 hover:shadow-[0_25px_60px_-15px_rgba(24,33,109,0.12)] hover:border-emerald-100 transition-all group flex flex-col h-full relative overflow-hidden">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50/40 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-125"></div>
                                            
                                            {/* Badges: Type & Year/Term */}
                                            <div className="absolute top-4 left-8 right-8 flex items-center justify-between">
                                                <div className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100/80 rounded-full text-[8px] font-black uppercase tracking-widest">
                                                    Scheme of Work
                                                </div>
                                                <div className="px-3 py-1 bg-slate-50 text-[#18216D] border border-slate-200/80 rounded-full text-[9px] font-black uppercase tracking-wider">
                                                    {sow.academic_year_name || 'Academic Year'} • {sow.term_name || 'Term'}
                                                </div>
                                            </div>

                                            <div className="flex items-start justify-between mb-6 mt-8 relative z-10">
                                                <div className="p-4 bg-emerald-50/60 rounded-2xl text-emerald-700 group-hover:bg-[#18216D] group-hover:text-white transition-all shadow-inner">
                                                    <AcademicCapIcon className="w-7 h-7 stroke-[2]" />
                                                </div>
                                            </div>

                                            <div className="flex-1 relative z-10">
                                                <h3 className="text-2xl font-black text-[#18216D] line-clamp-2 mb-3 tracking-tighter leading-tight italic">
                                                    {cleanTitle}
                                                </h3>
                                                <div className="flex flex-col gap-2 mb-6">
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-[#FFC425]"></span>
                                                        <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">
                                                            {sow.learning_area_name}
                                                        </span>
                                                    </div>
                                                    {sow.grade_level_name && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                                                            <span className="text-[10px] font-black text-indigo-700 uppercase tracking-wider">
                                                                {sow.grade_level_name}{sow.stream_name && sow.stream_name !== 'All Streams' ? ` (${sow.stream_name})` : ''}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                                        <span className="text-[10px] font-black text-emerald-700 uppercase tracking-wider">
                                                            {totalWeeks} {totalWeeks === 1 ? 'Week' : 'Weeks'} • {totalLessons} {totalLessons === 1 ? 'Lesson' : 'Lessons'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="pt-4 border-t border-slate-100 relative z-10 flex items-center justify-between gap-3">
                                                <button
                                                    onClick={() => navigate(`/teacher/scheme-of-work/${courseId}`)}
                                                    className="flex-1 py-3 px-4 bg-[#18216D] hover:bg-[#0D164F] text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-md transition-all flex items-center justify-center gap-2 group-hover:shadow-indigo-900/20"
                                                >
                                                    <span>View Scheme</span>
                                                    <ChevronRightIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                                                </button>
                                                <button
                                                    onClick={() => handleExportSOWPDF(sow.id)}
                                                    disabled={isExporting}
                                                    className="py-3 px-4 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                                                >
                                                    {isExporting ? (
                                                        <div className="w-3.5 h-3.5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                                    ) : (
                                                        <ArrowDownTrayIcon className="w-3.5 h-3.5 stroke-[2.5]" />
                                                    )}
                                                    <span>PDF</span>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Deletion Modal */}
            <Modal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, templateId: null })}
                title="Destroy Blueprint"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={handleDeleteTemplate}
                            className="px-10 py-5 bg-rose-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-600 transition-all shadow-xl shadow-rose-900/20"
                        >
                            Yes, Burn it
                        </button>
                        <button
                            onClick={() => setDeleteModal({ isOpen: false, templateId: null })}
                            className="px-10 py-5 bg-slate-100 text-[#18216D] rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-all"
                        >
                            Keep Vaulted
                        </button>
                    </>
                }
            >
                Are you sure you want to delete this blueprint? This action is IRREVERSIBLE and will permanently remove this lesson design from your vault.
            </Modal>

            {/* Assessment Modal */}
            {blueprintModal.isOpen && (
                <BlueprintCreator
                    learningAreas={learningAreas}
                    initialData={editTemplateData}
                    onCancel={() => {
                        setBlueprintModal({ ...blueprintModal, isOpen: false });
                        setEditTemplateData(null);
                    }}
                    onSuccess={(data) => {
                        if (data.criteria_ee) { // It's an assessment template
                            if (editTemplateData) {
                                setAssessmentTemplates(assessmentTemplates.map(t => t.id === data.id ? data : t));
                            } else {
                                setAssessmentTemplates([data, ...assessmentTemplates]);
                            }
                        } else {
                            setTemplates([data, ...templates]);
                        }
                        setBlueprintModal({ ...blueprintModal, isOpen: false });
                        setEditTemplateData(null);
                    }}
                />
            )}

            {/* Lesson Plan Modal for Creation */}
            {isCreatingLessonPlan && (
                <LessonPlanModal
                    courseId={''}
                    subStrand={{}}
                    onClose={() => setIsCreatingLessonPlan(false)}
                    onEditSuccess={() => {
                        fetchTemplates();
                        setIsCreatingLessonPlan(false);
                    }}
                />
            )}

            {/* Lesson Plan Modal for Editing */}
            {editLessonPlan && (
                <LessonPlanModal
                    courseId={editLessonPlan.learning_area_id || editLessonPlan.sub_strand?.strand?.learning_area || ''}
                    subStrand={{ id: editLessonPlan.sub_strand_id || editLessonPlan.sub_strand, name: editLessonPlan.sub_strand_name }}
                    editPlan={editLessonPlan}
                    onClose={() => setEditLessonPlan(null)}
                    onEditSuccess={() => {
                        fetchTemplates(); // Refresh the list
                    }}
                />
            )}
            <FloatingManualButton url="/manuals/Teacher_User_Manual.pdf" />
        </div>
    );
};

export default LessonPlanTemplateManager;
